import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../contexts/AuthContext';
import { toast } from "@/hooks/use-toast";
import { supabase } from '../integrations/supabase/client';

interface AnnouncementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AnnouncementDialog: React.FC<AnnouncementDialogProps> = ({ open, onOpenChange }) => {
  useUiLanguage();
  const { t } = useTranslation(['admin', 'common']);
  const { impersonation, profile } = useAuth();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [ageGroup, setAgeGroup] = useState<'all' | 'kids' | 'teens' | 'adults'>('all');
  const [selectedDojoId, setSelectedDojoId] = useState<string>('');
  const [availableDojos, setAvailableDojos] = useState<Array<{id: string, name: string}>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { sendNotification } = useNotifications();

  // Fetch available dojos for super users
  React.useEffect(() => {
    const fetchDojos = async () => {
      if (profile?.role === 'super_user' && open) {
        try {
          const { data: dojos, error } = await supabase
            .from('dojos')
            .select('id, name')
            .eq('is_active', true)
            .order('name');

          if (error) throw error;
          setAvailableDojos(dojos || []);

          // Auto-select the impersonated dojo or first dojo
          if (impersonation.active && impersonation.dojoId) {
            setSelectedDojoId(impersonation.dojoId);
          } else if (dojos && dojos.length > 0) {
            setSelectedDojoId(dojos[0].id);
          }
        } catch (error) {
          console.error('Error fetching dojos:', error);
        }
      }
    };

    fetchDojos();
  }, [profile?.role, open, impersonation.active, impersonation.dojoId]);

  const handleSendAnnouncement = async () => {
    if (!title.trim() || !message.trim()) {
      toast({
        title: t('admin:announcements.validationError'),
        description: t('admin:announcements.validationError'),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // DEBUG: Log the current state
      console.log('🔍 AnnouncementDialog Debug Info:');
      console.log('- Impersonation active:', impersonation.active);
      console.log('- Impersonation dojoId:', impersonation.dojoId);
      console.log('- Impersonation dojoName:', impersonation.dojoName);
      console.log('- Profile:', profile);
      console.log('- Profile role:', profile?.role);
      console.log('- Profile dojo_id:', profile?.dojo_id);

      // Get the dojo_id from impersonation context, selected dojo, or current profile
      const dojoId = profile?.role === 'super_user' && selectedDojoId
        ? selectedDojoId
        : impersonation.active
          ? impersonation.dojoId
          : profile?.dojo_id;
      console.log('- Final dojoId being used:', dojoId);

      if (!dojoId) {
        console.error('❌ No dojo context found');
        throw new Error(uiText("No dojo context found"));
      }

      // Get students based on age group from the correct dojo
      console.log('📝 Building query for dojo:', dojoId, 'age group:', ageGroup);
      let studentsQuery = supabase
        .from('profiles')
        .select('id, email, first_name, last_name, age_group')
        .eq('role', 'student')
        .eq('dojo_id', dojoId);

      // Apply age filtering based on selection
      if (ageGroup === 'kids') {
        studentsQuery = studentsQuery.eq('age_group', 'kids');
      } else if (ageGroup === 'teens') {
        studentsQuery = studentsQuery.eq('age_group', 'teens');
      } else if (ageGroup === 'adults') {
        studentsQuery = studentsQuery.eq('age_group', 'adults');
      }
      // For 'all', no additional filtering is applied

      const { data: students, error: studentsError } = await studentsQuery;

      console.log('📊 Query results:');
      console.log('- Students found:', students?.length || 0);
      console.log('- Students data:', students);
      console.log('- Query error:', studentsError);

      if (studentsError) {
        console.error('❌ Students query error:', studentsError);
        throw studentsError;
      }

      if (!students || students.length === 0) {
        const ageGroupText = ageGroup === 'kids' ? uiText("Kids (5-12)") : ageGroup === 'teens' ? uiText("Teens (13-17)") : ageGroup === 'adults' ? uiText("Adults (18+)") : 'selected';
        console.log('⚠️ No students found for age group:', ageGroup, 'in dojo:', dojoId);

        // Let's also check if there are ANY students in this dojo
        const { data: allStudents } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, age, dojo_id')
          .eq('role', 'student')
          .eq('dojo_id', dojoId);
        console.log('🔍 All students in dojo:', allStudents);

        toast({
          title: uiText("Notice"),
          description: uiText("No students found in the {{value0}} age group.", { value0: ageGroupText })
        });
        return;
      }

      // Create notifications in the database for filtered students
      const notifications = students.map(student => ({
        user_id: student.id,
        type: 'announcement',
        title: title.trim(),
        message: message.trim()
      }));

      const { error: notificationError } = await supabase
        .from('notifications')
        .insert(notifications);

      if (notificationError) {
        console.error('Error creating notifications:', notificationError);
        throw notificationError;
      }

      // Send email notifications using the edge function
      const success = await sendNotification({
        type: 'announcement',
        title: title.trim(),
        message: message.trim(),
        dojoId: dojoId,
        ageFilter: ageGroup
      });

      const ageGroupText = ageGroup === 'kids' ? uiText(" (Kids 5-12)") : ageGroup === 'teens' ? uiText(" (Teens 13-17)") : ageGroup === 'adults' ? uiText(" (Adults 18+)") : '';

      if (success) {
        toast({
          title: uiText("Announcement Sent"),
          description: uiText("Announcement has been sent to {{value0}} students{{value1}}!", { value0: students.length, value1: ageGroupText })
        });
      } else {
        toast({
          title: uiText("Partial Success"),
          description: uiText("Notifications created for {{value0}} students{{value1}}, but email sending failed.", { value0: students.length, value1: ageGroupText })
        });
      }

      // Always reset form and close dialog on success (notifications were created)
      setTitle('');
      setMessage('');
      setAgeGroup('all');
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending announcement:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to send announcement. Please try again."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{uiText("Send Announcement")}</DialogTitle>
          <DialogDescription>
            {uiText("Send an announcement to all students or a specific age group")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="announcement-title">{uiText("Title")}</Label>
            <Input
              id="announcement-title"
              placeholder={uiText("Enter announcement title")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Dojo Selector for Super Users */}
          {profile?.role === 'super_user' && availableDojos.length > 0 && (
            <div>
              <Label htmlFor="target-dojo">{uiText("Target Dojo")}</Label>
              <Select value={selectedDojoId} onValueChange={setSelectedDojoId}>
                <SelectTrigger>
                  <SelectValue placeholder={uiText("Select target dojo")} />
                </SelectTrigger>
                <SelectContent>
                  {availableDojos.map((dojo) => (
                    <SelectItem key={dojo.id} value={dojo.id}>
                      {dojo.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="ageGroup">{uiText("Target Age Group")}</Label>
            <Select value={ageGroup} onValueChange={(value: 'all' | 'kids' | 'teens' | 'adults') => setAgeGroup(value)}>
              <SelectTrigger>
                <SelectValue placeholder={uiText("Select age group")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{uiText("All Ages")}</SelectItem>
                <SelectItem value="kids">{uiText("Kids (5-12)")}</SelectItem>
                <SelectItem value="teens">{uiText("Teens (13-17)")}</SelectItem>
                <SelectItem value="adults">{uiText("Adults (18+)")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="announcement-message">{uiText("Message")}</Label>
            <Textarea
              id="announcement-message"
              placeholder={uiText("Enter your announcement message")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {uiText("Cancel")}</Button>
          <Button
            onClick={handleSendAnnouncement}
            disabled={isLoading || !title.trim() || !message.trim()}
            className="bg-red-900 hover:bg-red-800"
          >
            {isLoading ? uiText("Sending...") : uiText("Send Announcement")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementDialog;
